import { useEffect, useState } from 'react';
import { ApiError, api, type MaterialRow, type WhatsAppDraft } from '../api';
import { useI18n } from '../i18n';
import { EmptyState, ErrorState, Field, Modal, Skeleton, Spinner, Tabs, WhatsAppDialog } from '../components';
import { formatMinor, parseToMinor } from '../money';
import { IconSearch } from '../icons';
import { describe } from '../errors';
import { ConfirmRemove } from '../components/ConfirmRemove';
import { EditContact } from '../components/EditContact';
import { RecordPayment, type PayTo } from './Finance';

/** Categories a clinic already uses, plus room to name a new one. */
const FALLBACK_CATEGORIES = ['consumable', 'anaesthetic', 'restorative', 'endodontic', 'prosthetic', 'rotary'];

export function Inventory({
  clinicId,
  canWrite,
  permissions,
  currency,
}: {
  clinicId: string;
  canWrite: boolean;
  permissions: string[];
  currency: string;
}) {
  const { t } = useI18n();
  const can = (p: string) => permissions.includes(p);
  // Each button is shown to the person the server will let through: paying
  // is finance, posting a bill and receiving goods are their own rights.
  const canPay = can('finance.payment.record');
  const canPost = can('purchase.post');
  const canReceive = can('purchase.receive');
  const [tab, setTab] = useState<'materials' | 'suppliers' | 'purchases' | 'alerts'>('materials');
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    void api.inventoryAlerts().then((a) => setAlertCount(a.lowStock.length + a.expiring.length)).catch(() => {});
  }, [clinicId]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.inventory.title}</h1>
        </div>
      </div>
      <Tabs
        value={tab}
        onChange={(v) => setTab(v as typeof tab)}
        tabs={[
          { value: 'materials', label: t.inventory.materials },
          { value: 'suppliers', label: t.inventory.suppliers },
          { value: 'purchases', label: t.inventory.purchases },
          { value: 'alerts', label: t.inventory.alerts, badge: alertCount },
        ]}
      />
      {tab === 'materials' ? <Materials clinicId={clinicId} canWrite={canWrite} /> : null}
      {tab === 'suppliers' ? (
        <Suppliers clinicId={clinicId} canWrite={can('supplier.manage')} canPay={canPay} currency={currency} />
      ) : null}
      {tab === 'purchases' ? (
        <Purchases
          clinicId={clinicId}
          canWrite={canWrite}
          canDelete={can('purchase.delete')}
          canPay={canPay}
          canPost={canPost}
          canReceive={canReceive}
          currency={currency}
        />
      ) : null}
      {tab === 'alerts' ? <Alerts clinicId={clinicId} /> : null}
    </>
  );
}

const LEVEL_TONE: Record<string, string> = { ok: 'ok', low: 'warn', out: 'danger', negative: 'danger' };

function Materials({ clinicId, canWrite }: { clinicId: string; canWrite: boolean }) {
  const { t, locale, formatNumber } = useI18n();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.materials>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [consuming, setConsuming] = useState<MaterialRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingItem, setEditingItem] = useState<MaterialRow | null>(null);
  const [removingItem, setRemovingItem] = useState<MaterialRow | null>(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [draft, setDraft] = useState<WhatsAppDraft | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setData(null);
    api.materials().then(setData).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  // Asking for a price opens a small dialog to choose the supplier, rather
  // than guessing the last one and sending to it without saying so.
  const [asking, setAsking] = useState<MaterialRow | null>(null);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;
  if (!data) return <div className="card"><Skeleton rows={6} /></div>;

  const hasNegative = data.materials.some((m) => m.level === 'negative');

  /*
    Filtered in the browser, not on the server.

    A clinic's material catalogue is hundreds of rows, not hundreds of
    thousands — the whole list is already loaded to compute on-hand totals, so
    a round trip per keystroke would buy nothing. Matching covers the SKU, both
    names and the location, because people look things up by whichever of those
    they happen to remember.
  */
  const needle = query.trim().toLowerCase();
  const categories = [...new Set(data.materials.map((m) => m.category))].sort();
  const visible = data.materials.filter((m) => {
    if (category && m.category !== category) return false;
    if (lowOnly && m.level === 'ok') return false;
    if (!needle) return true;
    return [m.sku, m.nameEn, m.nameAr, m.location ?? ''].join(' ').toLowerCase().includes(needle);
  });

  return (
    <>
      {hasNegative ? (
        <div className="notice danger" style={{ marginBottom: 12 }} role="alert">
          <span>{t.inventory.negativeNote}</span>
        </div>
      ) : null}

      {/*
        Adding an item is a first-class action, not something you can only reach
        through a purchase order. A clinic that starts using a new material
        needs it in the catalogue before anyone buys any — and adding it here
        creates NO stock, because stock comes from movements.
      */}
      <div className="toolbar">
        <div className="search-box">
          <IconSearch size={16} className="icon" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.inventory.search}
            aria-label={t.inventory.search}
          />
        </div>

        {categories.length > 1 ? (
          <select
            className="select-inline"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label={t.inventory.category}
          >
            <option value="">{t.inventory.allCategories}</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        ) : null}

        <label className="row" style={{ gap: 7 }}>
          <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} />
          <span style={{ fontSize: 13 }}>{t.inventory.lowStock}</span>
        </label>

        <div className="toolbar-spacer" />
        {canWrite ? (
          <button type="button" className="btn" onClick={() => setCreating(true)}>
            + {t.inventory.newItem}
          </button>
        ) : null}
      </div>

      <div className="card">
        <div className="card-head">
          <h2>
            {t.inventory.materials}
            <span className="count-chip">{visible.length}</span>
          </h2>
          <span className="badge">
            {t.inventory.costingMethod}: {data.costingMethod.replace(/_/g, ' ')}
          </span>
        </div>
        {visible.length === 0 ? (
          <EmptyState
            title={data.materials.length === 0 ? t.inventory.noMaterials : t.inventory.noMatches}
            action={
              canWrite ? (
                <button type="button" className="btn" onClick={() => setCreating(true)}>
                  + {t.inventory.newItem}
                </button>
              ) : null
            }
          />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.inventory.sku}</th>
                  <th>{t.inventory.name}</th>
                  <th>{t.inventory.onHand}</th>
                  <th>{t.inventory.threshold}</th>
                  <th>{t.inventory.unitCost}</th>
                  <th>{t.inventory.value}</th>
                  <th>{t.inventory.supplier}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => (
                  <tr key={m.id}>
                    <td className="ltr-value">{m.sku}</td>
                    <td>
                      <strong>{locale === 'ar' ? m.nameAr : m.nameEn}</strong>
                      {m.active === false ? (
                        <span className="badge" style={{ marginInlineStart: 8 }}>
                          {t.remove.retired}
                        </span>
                      ) : null}
                      {m.location ? (
                        <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>{m.location}</span>
                      ) : null}
                    </td>
                    <td>
                      <span className={`badge ${LEVEL_TONE[m.level] ?? ''}`}>
                        {formatNumber(m.onHand)} {m.baseUnit}
                      </span>
                    </td>
                    <td>{formatNumber(m.reorderThreshold)}</td>
                    <td className="ltr-value">{formatMinor(m.unitCostMinor, data.currency, locale)}</td>
                    <td className="ltr-value">{formatMinor(m.valueMinor, data.currency, locale)}</td>
                    <td>{m.lastSupplier?.name ?? m.preferredSupplier?.name ?? '—'}</td>
                    <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                      {canWrite ? (
                        <button
                          type="button"
                          className="btn-link"
                          style={{ marginInlineEnd: 14 }}
                          onClick={() => setConsuming(m)}
                        >
                          {t.inventory.consume}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn-link"
                        style={{ marginInlineEnd: 14 }}
                        onClick={() => setAsking(m)}
                      >
                        {t.inventory.askPrice}
                      </button>
                      {canWrite ? (
                        <>
                          <button
                            type="button"
                            className="btn-link"
                            style={{ marginInlineEnd: 14 }}
                            onClick={() => setEditingItem(m)}
                          >
                            {t.remove.edit}
                          </button>
                          {m.active === false ? (
                            <button
                              type="button"
                              className="btn-link"
                              onClick={async () => {
                                await api.updateMaterial(m.id, { active: true });
                                setNonce((n) => n + 1);
                              }}
                            >
                              {t.remove.reactivate}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn-link danger"
                              onClick={() => setRemovingItem(m)}
                            >
                              {t.remove.confirm}
                            </button>
                          )}
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {consuming ? (
        <ConsumeDialog
          material={consuming}
          onClose={() => setConsuming(null)}
          onDone={() => {
            setConsuming(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {creating ? (
        <NewMaterialDialog
          categories={categories.length > 0 ? categories : FALLBACK_CATEGORIES}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {asking ? (
        <AskPrice
          material={asking}
          onClose={() => setAsking(null)}
          onDraft={(d) => {
            setAsking(null);
            setDraft(d);
          }}
        />
      ) : null}

      {editingItem ? (
        <EditMaterial
          material={editingItem}
          categories={categories.length > 0 ? categories : FALLBACK_CATEGORIES}
          onClose={() => setEditingItem(null)}
          onDone={() => {
            setEditingItem(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {removingItem ? (
        <ConfirmRemove
          title={t.remove.deleteMaterial}
          what={`${removingItem.sku} · ${locale === 'ar' ? removingItem.nameAr : removingItem.nameEn}`}
          onConfirm={() => api.deleteMaterial(removingItem.id)}
          onClose={() => setRemovingItem(null)}
          onDone={() => {
            setRemovingItem(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {draft ? <WhatsAppDialog draft={draft} onClose={() => setDraft(null)} /> : null}
    </>
  );
}

/**
 * Correcting an item.
 *
 * The same fields as creating one, minus the stock: stock is the sum of its
 * movements and has never been typed in here, so there is nothing to edit.
 * Sends only what changed, so the audit entry names the field that actually
 * moved rather than all eleven.
 */
function EditMaterial({
  material,
  categories,
  onClose,
  onDone,
}: {
  material: MaterialRow;
  categories: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const initial = {
    sku: material.sku,
    nameEn: material.nameEn,
    nameAr: material.nameAr,
    category: material.category ?? '',
    reorderThreshold: String(material.reorderThreshold),
    storageLocation: material.location ?? '',
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patch: Record<string, unknown> = {};
  if (form.sku.trim() !== initial.sku) patch.sku = form.sku.trim();
  if (form.nameEn.trim() !== initial.nameEn) patch.nameEn = form.nameEn.trim();
  if (form.nameAr.trim() !== initial.nameAr) patch.nameAr = form.nameAr.trim();
  if (form.category !== initial.category) patch.category = form.category;
  if (form.reorderThreshold !== initial.reorderThreshold) {
    patch.reorderThreshold = Number(form.reorderThreshold) || 0;
  }
  if (form.storageLocation.trim() !== initial.storageLocation) {
    patch.storageLocation = form.storageLocation.trim() || null;
  }

  return (
    <Modal
      title={t.remove.editMaterial}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || Object.keys(patch).length === 0 || form.nameEn.trim() === ''}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.updateMaterial(material.id, patch);
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.settings.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}
        <div className="grid-2">
          <Field label={t.inventory.sku} required>
            <input
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              className="ltr-value"
            />
          </Field>
          <Field label={t.inventory.category}>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t.inventory.name} required>
            <input value={form.nameEn} onChange={(e) => setForm({ ...form, nameEn: e.target.value })} />
          </Field>
          <Field label={`${t.inventory.name} (العربية)`}>
            <input
              value={form.nameAr}
              onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
              dir="rtl"
            />
          </Field>
          <Field label={t.inventory.threshold}>
            <input
              value={form.reorderThreshold}
              onChange={(e) => setForm({ ...form, reorderThreshold: e.target.value })}
              inputMode="numeric"
              className="ltr-value"
            />
          </Field>
          <Field label={t.inventory.location} hint={t.patients.optional}>
            <input
              value={form.storageLocation}
              onChange={(e) => setForm({ ...form, storageLocation: e.target.value })}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Add an item to the catalogue.
 *
 * It starts with NO stock, and the dialog says so. Stock exists only as the sum
 * of movements, so there is nowhere here to type an opening quantity — the
 * first goods receipt brings the item into stock, and that receipt is the
 * record of where it came from.
 *
 * The pack size is the part people get wrong: "a box of 100 gloves" is 100 base
 * units per pack, and recording use of one pack must deduct 100, not 1.
 */
function NewMaterialDialog({
  onClose,
  onDone,
  categories,
}: {
  onClose: () => void;
  onDone: () => void;
  categories: string[];
}) {
  const { t } = useI18n();
  // A free-text category quietly creates "Consumable", "consumable" and
  // "consumables" as three different things, and the filter above then shows
  // all three. Picking from what the clinic already uses is the default;
  // naming a new one is a deliberate extra step.
  const [newCategory, setNewCategory] = useState(false);
  const [form, setForm] = useState({
    sku: '',
    nameEn: '',
    nameAr: '',
    category: 'consumable',
    baseUnit: 'piece',
    packUnit: 'box',
    packSize: '1',
    reorderThreshold: '0',
    storageLocation: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<Record<string, string>>({});

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit() {
    setBusy(true);
    setError(null);
    setIssues({});
    try {
      await api.createMaterial({
        sku: form.sku.trim(),
        nameEn: form.nameEn.trim(),
        // An Arabic name is required by the API; defaulting to the English one
        // is better than refusing to save, and it stays editable.
        nameAr: form.nameAr.trim() || form.nameEn.trim(),
        category: form.category.trim() || 'consumable',
        baseUnit: form.baseUnit.trim() || 'piece',
        packUnit: form.packUnit.trim() || 'box',
        packSize: Number(form.packSize) || 1,
        reorderThreshold: Number(form.reorderThreshold) || 0,
        storageLocation: form.storageLocation.trim() || undefined,
      });
      onDone();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        if (e.issues) setIssues(Object.fromEntries(e.issues.map((i) => [i.path, i.message])));
      } else {
        setError(describe(e, t));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t.inventory.newItem}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void submit()}
            disabled={busy || !form.sku.trim() || !form.nameEn.trim()}
          >
            {busy ? <Spinner /> : t.patients.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        <div className="notice">
          <span>{t.inventory.newItemHint}</span>
        </div>

        <div className="grid-2">
          <Field label={t.inventory.sku} required error={issues.sku}>
            <input value={form.sku} onChange={set('sku')} className="ltr-value" autoFocus />
          </Field>
          <Field label={t.inventory.category} required>
            {newCategory ? (
              <input value={form.category} onChange={set('category')} autoFocus placeholder={t.inventory.newCategory} />
            ) : (
              <select
                value={form.category}
                onChange={(e) => {
                  if (e.target.value === '__new') {
                    setNewCategory(true);
                    setForm((f) => ({ ...f, category: '' }));
                  } else {
                    setForm((f) => ({ ...f, category: e.target.value }));
                  }
                }}
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                <option value="__new">+ {t.inventory.newCategory}</option>
              </select>
            )}
          </Field>
          <Field label={t.inventory.itemName} required error={issues.nameEn}>
            <input value={form.nameEn} onChange={set('nameEn')} />
          </Field>
          <Field label={`${t.inventory.itemName} (العربية)`} hint={t.patients.optional}>
            <input value={form.nameAr} onChange={set('nameAr')} dir="rtl" />
          </Field>
          <Field label={t.inventory.baseUnit} required>
            <input value={form.baseUnit} onChange={set('baseUnit')} />
          </Field>
          <Field label={t.inventory.packUnit} required>
            <input value={form.packUnit} onChange={set('packUnit')} />
          </Field>
          <Field label={t.inventory.packSize} hint={t.inventory.packSizeHint} required error={issues.packSize}>
            <input value={form.packSize} onChange={set('packSize')} inputMode="numeric" className="ltr-value" />
          </Field>
          <Field label={t.inventory.reorderLevel}>
            <input
              value={form.reorderThreshold}
              onChange={set('reorderThreshold')}
              inputMode="numeric"
              className="ltr-value"
            />
          </Field>
          <Field label={t.inventory.location} hint={t.patients.optional}>
            <input value={form.storageLocation} onChange={set('storageLocation')} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Recording use.
 *
 * The dialog is explicit about units: taking one PACK deducts its configured
 * pack size, not a guessed whole box (spec §13).
 */
function ConsumeDialog({
  material,
  onClose,
  onDone,
}: {
  material: MaterialRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, locale } = useI18n();
  const [mode, setMode] = useState<'base' | 'pack'>('base');
  const [qty, setQty] = useState('1');
  const [reason, setReason] = useState('Clinical use');
  const [occurredOn, setOccurredOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ onHand: number; warning: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.recordMovement({
        materialId: material.id,
        kind: 'issue',
        ...(mode === 'pack' ? { packQuantity: Number(qty) } : { quantity: Number(qty) }),
        reason,
        // A movement dated wrongly makes the on-hand HISTORY lie even though
        // the running total stays right, so the date is editable.
        occurredAt: `${occurredOn}T12:00:00.000Z`,
      });
      setResult({ onHand: r.onHand, warning: r.warning });
      setTimeout(onDone, 900);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${t.inventory.consume} — ${locale === 'ar' ? material.nameAr : material.nameEn}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy || !qty}>
            {busy ? <Spinner /> : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        {result ? (
          <div className={result.warning ? 'notice danger' : 'notice'}>
            <span>
              {t.inventory.onHand}: <strong>{result.onHand}</strong> {material.baseUnit}
              {result.warning ? ` — ${result.warning}` : ''}
            </span>
          </div>
        ) : null}

        <div className="row" style={{ gap: 6 }}>
          <button
            type="button"
            className={mode === 'base' ? 'btn btn-sm' : 'btn btn-ghost btn-sm'}
            onClick={() => setMode('base')}
          >
            {material.baseUnit}
          </button>
          <button
            type="button"
            className={mode === 'pack' ? 'btn btn-sm' : 'btn btn-ghost btn-sm'}
            onClick={() => setMode('pack')}
          >
            {material.packUnit} ({material.packSize} {material.baseUnit})
          </button>
        </div>

        <Field
          label={t.inventory.quantity}
          hint={
            mode === 'pack'
              ? `${Number(qty) || 0} × ${material.packSize} = ${(Number(qty) || 0) * material.packSize} ${material.baseUnit}`
              : undefined
          }
          required
        >
          <input value={qty} onChange={(e) => setQty(e.target.value)} className="ltr-value" inputMode="numeric" />
        </Field>

        <div className="grid-2">
          <Field label={t.finance.description}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <Field label={t.inventory.usedOn} hint={t.inventory.usedOnHint}>
            <input
              type="date"
              value={occurredOn}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setOccurredOn(e.target.value)}
              className="ltr-value"
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

type SupplierRow = Awaited<ReturnType<typeof api.suppliers>>['suppliers'][number];

function Suppliers({
  clinicId,
  canWrite,
  canPay,
  currency,
}: {
  clinicId: string;
  canWrite: boolean;
  canPay: boolean;
  currency: string;
}) {
  const { t, locale } = useI18n();
  const [rows, setRows] = useState<SupplierRow[] | null>(null);
  const [editing, setEditing] = useState<SupplierRow | null>(null);
  const [removing, setRemoving] = useState<SupplierRow | null>(null);
  const [paying, setPaying] = useState<PayTo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', whatsappPhone: '', address: '' });
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setRows(null);
    api.suppliers().then((r) => setRows(r.suppliers)).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        {canWrite ? (
          <button type="button" className="btn" onClick={() => setAdding(true)}>
            + {t.inventory.newSupplier}
          </button>
        ) : null}
      </div>

      <div className="card">
        {rows === null ? (
          <Skeleton rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.inventory.noSuppliers} />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.inventory.name}</th>
                  <th>{t.patients.phone}</th>
                  <th>{t.finance.billed}</th>
                  <th>{t.finance.paid}</th>
                  <th>{t.lab.outstanding}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} style={{ opacity: s.active ? 1 : 0.55 }}>
                    <td>
                      <strong>{s.name}</strong>
                      {s.active ? null : (
                        <span className="badge" style={{ marginInlineStart: 8 }}>
                          {t.remove.retired}
                        </span>
                      )}
                    </td>
                    <td className="ltr-value">{s.whatsappPhone ?? s.phone ?? '—'}</td>
                    <td className="ltr-value">{formatMinor(s.billedMinor, currency, locale)}</td>
                    <td className="ltr-value">{formatMinor(s.paidMinor, currency, locale)}</td>
                    <td className="ltr-value">
                      <strong style={{ color: s.outstandingMinor > 0 ? 'var(--warn)' : 'var(--ok)' }}>
                        {formatMinor(s.outstandingMinor, currency, locale)}
                      </strong>
                    </td>
                    <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                      {/* The outstanding figure was shown with no way to act
                          on it. Settling it starts here, on the row. */}
                      {canPay && s.outstandingMinor > 0 ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ marginInlineEnd: 14 }}
                          onClick={() =>
                            setPaying({
                              kind: 'supplier',
                              id: s.id,
                              name: s.name,
                              outstandingMinor: s.outstandingMinor,
                              currency,
                            })
                          }
                        >
                          {t.finance.pay}
                        </button>
                      ) : null}
                      {canWrite ? (
                        <>
                          <button
                            type="button"
                            className="btn-link"
                            style={{ marginInlineEnd: 14 }}
                            onClick={() => setEditing(s)}
                          >
                            {t.remove.edit}
                          </button>
                          {/* A retired supplier can be brought back. Without
                              this, "kept and marked inactive" would be a
                              promise the screen could not honour. */}
                          {s.active ? (
                            <button type="button" className="btn-link danger" onClick={() => setRemoving(s)}>
                              {t.remove.confirm}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn-link"
                              onClick={async () => {
                                await api.updateSupplier(s.id, { active: true });
                                setNonce((n) => n + 1);
                              }}
                            >
                              {t.remove.reactivate}
                            </button>
                          )}
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {paying ? (
        <RecordPayment
          payTo={paying}
          onClose={() => setPaying(null)}
          onDone={() => {
            setPaying(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {editing ? (
        <EditContact
          title={t.remove.editSupplier}
          initial={editing}
          onSave={(patch) => api.updateSupplier(editing.id, patch)}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {removing ? (
        <ConfirmRemove
          title={t.remove.deleteSupplier}
          what={removing.name}
          onConfirm={() => api.deleteSupplier(removing.id)}
          onClose={() => setRemoving(null)}
          onDone={() => {
            setRemoving(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {adding ? (
        <Modal
          title={t.inventory.newSupplier}
          onClose={() => setAdding(false)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                {t.patients.cancel}
              </button>
              <button
                type="button"
                className="btn"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api.createSupplier(form);
                    setAdding(false);
                    setForm({ name: '', phone: '', whatsappPhone: '', address: '' });
                    setNonce((n) => n + 1);
                  } catch (e) {
                    setError(describe(e, t));
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy || !form.name}
              >
                {busy ? <Spinner /> : t.chart.save}
              </button>
            </>
          }
        >
          <div className="stack">
            <Field label={t.inventory.name} required>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <div className="grid-2">
              <Field label={t.patients.phone}>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ltr-value" />
              </Field>
              <Field label={t.patients.whatsapp}>
                <input value={form.whatsappPhone} onChange={(e) => setForm({ ...form, whatsappPhone: e.target.value })} className="ltr-value" />
              </Field>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

/**
 * Purchasing.
 *
 * The action selector is the point of this screen: posting a bill and receiving
 * goods are different events, and the interface makes the user choose which one
 * actually happened (spec §13).
 */
type PurchaseRow = Awaited<ReturnType<typeof api.purchases>>['purchases'][number];

function Purchases({
  clinicId,
  canWrite,
  canDelete,
  canPay,
  canPost,
  canReceive,
  currency,
}: {
  clinicId: string;
  canWrite: boolean;
  canDelete: boolean;
  canPay: boolean;
  canPost: boolean;
  canReceive: boolean;
  currency: string;
}) {
  const { t, locale, formatDate } = useI18n();
  const [rows, setRows] = useState<PurchaseRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<PurchaseRow | null>(null);
  const [payingBill, setPayingBill] = useState<PayTo | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function finish(id: string, half: 'post' | 'receive') {
    setBusyId(id);
    setError(null);
    try {
      if (half === 'post') await api.postPurchase(id);
      else await api.receivePurchase(id);
      setNonce((n) => n + 1);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusyId(null);
    }
  }
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setRows(null);
    api.purchases().then((r) => setRows(r.purchases)).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        {canPost || canReceive ? (
          <button type="button" className="btn" onClick={() => setCreating(true)}>
            + {t.inventory.newPurchase}
          </button>
        ) : null}
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <span>{t.inventory.actionHint}</span>
      </div>

      <div className="card">
        {rows === null ? (
          <Skeleton rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.inventory.noPurchases} />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.inventory.invoiceNumber}</th>
                  <th>{t.inventory.supplier}</th>
                  <th>{t.finance.issued}</th>
                  <th>{t.finance.total}</th>
                  <th>{t.finance.status}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td className="ltr-value">{p.number}</td>
                    <td>{p.supplier}</td>
                    <td>{formatDate(p.invoicedOn)}</td>
                    <td className="ltr-value">
                      {formatMinor(p.totalMinor, p.currency, locale)}
                      {p.status === 'posted' ? (
                        <span style={{ display: 'block', color: p.outstandingMinor > 0 ? 'var(--warn)' : 'var(--ok)', fontSize: 12 }}>
                          {t.finance.outstanding}: {formatMinor(p.outstandingMinor, p.currency, locale)}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <span className={`badge ${p.status === 'posted' ? 'ok' : ''}`}>
                        {p.status === 'posted' ? t.finance.posted : t.finance.draft}
                      </span>
                      <span className={`badge ${p.received ? 'ok' : 'warn'}`} style={{ marginInlineStart: 6 }}>
                        {p.received ? t.inventory.received : t.inventory.billedOnly}
                      </span>
                    </td>
                    <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                      {/* Pay THIS bill, not the supplier's whole balance. */}
                      {canPay && p.status === 'posted' && p.outstandingMinor > 0 ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ marginInlineEnd: 10 }}
                          onClick={() =>
                            setPayingBill({
                              kind: 'supplier',
                              id: p.supplierId,
                              name: p.supplier,
                              outstandingMinor: p.outstandingMinor,
                              currency: p.currency,
                              purchaseInvoiceId: p.id,
                              purchaseNumber: p.number,
                            })
                          }
                        >
                          {t.finance.pay}
                        </button>
                      ) : null}
                      {/* Whichever half was not done at entry can be done now.
                          A bill entered as "goods received" owes the supplier
                          nothing until it is posted; one entered as "bill
                          posted" has no stock until it is received. */}
                      {canPost && p.status !== 'posted' ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ marginInlineEnd: 10 }}
                          disabled={busyId === p.id}
                          title={t.inventory.postBillHint}
                          onClick={() => void finish(p.id, 'post')}
                        >
                          {busyId === p.id ? <Spinner /> : t.inventory.postBill}
                        </button>
                      ) : null}
                      {canReceive && !p.received ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          style={{ marginInlineEnd: 10 }}
                          disabled={busyId === p.id}
                          title={t.inventory.receiveGoodsHint}
                          onClick={() => void finish(p.id, 'receive')}
                        >
                          {busyId === p.id ? <Spinner /> : t.inventory.receiveGoods}
                        </button>
                      ) : null}
                      {/* Draft only. A posted bill owes the supplier money and
                          may have moved stock; the API refuses it and says so,
                          but offering the button would be a poor way to find
                          that out. */}
                      {canDelete && p.status === 'draft' && !p.received ? (
                        <button type="button" className="btn-link danger" onClick={() => setRemoving(p)}>
                          {t.remove.delete}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {payingBill ? (
        <RecordPayment
          payTo={payingBill}
          onClose={() => setPayingBill(null)}
          onDone={() => {
            setPayingBill(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {removing ? (
        <ConfirmRemove
          title={t.remove.deletePurchase}
          what={`${removing.number} · ${removing.supplier}`}
          actionLabel={t.remove.delete}
          onConfirm={() => api.deletePurchase(removing.id)}
          onClose={() => setRemoving(null)}
          onDone={() => {
            setRemoving(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {creating ? (
        <NewPurchase currency={currency}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

function NewPurchase({ currency, onClose, onCreated }: { currency: string; onClose: () => void; onCreated: () => void }) {
  const { t, locale } = useI18n();
  const [suppliers, setSuppliers] = useState<Awaited<ReturnType<typeof api.suppliers>>['suppliers']>([]);
  const [materials, setMaterials] = useState<MaterialRow[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [number, setNumber] = useState('');
  const [action, setAction] = useState<'post_bill' | 'confirm_receipt' | 'post_and_receive'>('post_and_receive');
  const [lines, setLines] = useState([{ materialId: '', packQuantity: '1', unitCost: '' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "+ New item" from inside a purchase line: the item does not exist yet
  // because this is the first time it is being bought.
  const [addingItemFor, setAddingItemFor] = useState<number | null>(null);

  useEffect(() => {
    void Promise.all([api.suppliers(), api.materials()]).then(([s, m]) => {
      setSuppliers(s.suppliers.filter((x) => x.active));
      setMaterials(m.materials.filter((x) => x.active !== false));
      setSupplierId(s.suppliers[0]?.id ?? '');
      if (m.materials[0]) setLines([{ materialId: m.materials[0].id, packQuantity: '1', unitCost: '' }]);
    });
  }, []);

  async function reloadMaterialsAndSelect(index: number) {
    const m = await api.materials();
    const active = m.materials.filter((x) => x.active !== false);
    setMaterials(active);
    // The newest item is the one just created.
    const newest = [...m.materials].sort((a, b) => (a.id < b.id ? 1 : -1))[0];
    if (newest) {
      setLines((l) => l.map((line, i) => (i === index ? { ...line, materialId: newest.id } : line)));
    }
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.recordPurchase({
        supplierId,
        number,
        action,
        lines: lines
          .filter((l) => l.materialId && l.unitCost)
          .map((l) => ({
            materialId: l.materialId,
            packQuantity: Number(l.packQuantity),
            unitCostMinor: parseToMinor(l.unitCost, currency) ?? 0,
          })),
      });
      onCreated();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t.inventory.newPurchase}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy || !number || !supplierId}>
            {busy ? <><Spinner /> {t.patients.saving}</> : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}

        <div className="grid-2">
          <Field label={t.inventory.supplier} required>
            <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </Field>
          <Field label={t.inventory.invoiceNumber} required>
            <input value={number} onChange={(e) => setNumber(e.target.value)} className="ltr-value" />
          </Field>
        </div>

        <Field label={t.inventory.action} hint={t.inventory.actionHint} required>
          <select value={action} onChange={(e) => setAction(e.target.value as typeof action)}>
            {(['post_and_receive', 'post_bill', 'confirm_receipt'] as const).map((a) => (
              <option key={a} value={a}>
                {(t.inventory.actions as Record<string, string>)[a]}
              </option>
            ))}
          </select>
        </Field>

        <div className="section-label">{t.inventory.materials}</div>
        {lines.map((line, idx) => {
          const material = materials.find((m) => m.id === line.materialId);
          return (
            <div key={idx} className="grid-3">
              <Field label={t.inventory.name}>
                <select
                  value={line.materialId}
                  onChange={(e) => {
                    if (e.target.value === '__new') {
                      setAddingItemFor(idx);
                      return;
                    }
                    const next = [...lines];
                    next[idx] = { ...line, materialId: e.target.value };
                    setLines(next);
                  }}
                >
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.sku} · {locale === 'ar' ? m.nameAr : m.nameEn}
                    </option>
                  ))}
                  <option value="__new">{t.inventory.newItemInline}</option>
                </select>
              </Field>
              <Field
                label={t.inventory.packs}
                hint={material ? `× ${material.packSize} ${material.baseUnit}` : undefined}
              >
                <input
                  value={line.packQuantity}
                  onChange={(e) => {
                    const next = [...lines];
                    next[idx] = { ...line, packQuantity: e.target.value };
                    setLines(next);
                  }}
                  className="ltr-value"
                  inputMode="numeric"
                />
              </Field>
              <Field label={t.inventory.unitCost}>
                <input
                  value={line.unitCost}
                  onChange={(e) => {
                    const next = [...lines];
                    next[idx] = { ...line, unitCost: e.target.value };
                    setLines(next);
                  }}
                  className="ltr-value"
                  inputMode="decimal"
                />
              </Field>
            </div>
          );
        })}

        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setLines([...lines, { materialId: materials[0]?.id ?? '', packQuantity: '1', unitCost: '' }])}
        >
          + {t.inventory.materials}
        </button>
      </div>

      {addingItemFor !== null ? (
        <NewMaterialDialog
          categories={[...new Set(materials.map((m) => m.category))].sort()}
          onClose={() => setAddingItemFor(null)}
          onDone={() => {
            const index = addingItemFor;
            setAddingItemFor(null);
            void reloadMaterialsAndSelect(index);
          }}
        />
      ) : null}
    </Modal>
  );
}

/**
 * Ask a supplier for a price.
 *
 * It used to pick the last supplier silently and open a WhatsApp draft to
 * them. Now it asks which one, and how many packs, because the whole point
 * of asking for a price is usually to compare.
 */
function AskPrice({
  material,
  onClose,
  onDraft,
}: {
  material: MaterialRow;
  onClose: () => void;
  onDraft: (draft: WhatsAppDraft) => void;
}) {
  const { t, locale } = useI18n();
  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [supplierId, setSupplierId] = useState(material.lastSupplier?.id ?? material.preferredSupplier?.id ?? '');
  const [quantity, setQuantity] = useState('1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.suppliers().then((r) => {
      const active = r.suppliers.filter((x) => x.active);
      setSuppliers(active);
      setSupplierId((cur) => cur || active[0]?.id || '');
    });
  }, []);

  return (
    <Modal
      title={`${t.inventory.askPriceTitle} — ${locale === 'ar' ? material.nameAr : material.nameEn}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !supplierId}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const r = await api.priceRequest({
                  supplierId,
                  locale,
                  items: [{ materialId: material.id, quantity: Math.max(1, Number(quantity) || 1) }],
                });
                onDraft(r.draft);
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.inventory.askPrice}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        <Field label={t.inventory.askWhichSupplier} required>
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}{s.whatsappPhone || s.phone ? '' : ' (no WhatsApp)'}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.inventory.askQuantity} hint={`${material.packUnit} × ${material.packSize} ${material.baseUnit}`}>
          <input value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="numeric" className="ltr-value" />
        </Field>
      </div>
    </Modal>
  );
}

function Alerts({ clinicId }: { clinicId: string }) {
  const { t, locale, formatDate, formatNumber } = useI18n();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.inventoryAlerts>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    api.inventoryAlerts().then(setData).catch((e) => setError(describe(e, t)));
  }, [clinicId, t]);

  if (error) return <ErrorState message={error} />;
  if (!data) return <div className="card"><Skeleton rows={4} /></div>;

  const nothing = data.lowStock.length === 0 && data.expiring.length === 0;

  return (
    <div className="stack">
      {nothing ? (
        <div className="card">
          <EmptyState title={t.inventory.allGood} />
        </div>
      ) : null}

      {data.lowStock.length > 0 ? (
        <div className="card">
          <div className="card-head">
            <h2>{t.inventory.lowStock}</h2>
            <span className="badge warn">{data.lowStock.length}</span>
          </div>
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.inventory.sku}</th>
                  <th>{t.inventory.name}</th>
                  <th>{t.inventory.onHand}</th>
                  <th>{t.inventory.threshold}</th>
                </tr>
              </thead>
              <tbody>
                {data.lowStock.map((m) => (
                  <tr key={m.id}>
                    <td className="ltr-value">{m.sku}</td>
                    <td>{locale === 'ar' ? m.nameAr : m.nameEn}</td>
                    <td>
                      <span className={`badge ${LEVEL_TONE[m.level] ?? ''}`}>
                        {formatNumber(m.onHand)} {m.unit}
                      </span>
                    </td>
                    <td>{formatNumber(m.threshold)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {data.expiring.length > 0 ? (
        <div className="card">
          <div className="card-head">
            <h2>{t.inventory.expiring}</h2>
          </div>
          <div className="card-body flush table-scroll">
            <table>
              <tbody>
                {data.expiring.map((b) => (
                  <tr key={b.batchId}>
                    <td>{locale === 'ar' ? b.nameAr : b.nameEn}</td>
                    <td className="ltr-value">{b.lot}</td>
                    <td>{formatDate(b.expiresOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

